import { Box, Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  CardComponent,
  Edit,
  GeneralCardComponent,
  PageTitleAndSubTitle,
  TextField,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { observer } from 'mobx-react';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { useNavigate, useLocation } from 'react-router-dom';
import { TREND_ANALYSIS_INTERNAL } from '../../core-utils/routes';
import ComparisonTable from '../../_components/atoms/ComparisonTable';
import properties from '../../core-utils/properties';
import axios from 'axios';
import { DueDiligenceDataType } from '../../core-utils/helper-utils/recommendedAppsHelper';
import { RECOMMENDED_APPS } from '../../core-utils/messages/RecommendedAppConstants';
import Loader from '../../_components/molecules/Loader';
import { Pagination } from '@mui/material';
import NoDoc from '../../_components/atoms/Icons/NoDocuments';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { compilationDataProps } from '../TermCompilerPage';
import { useStores } from '../../stores';
import usePlanDates from '../../core-utils/custom-hooks/PlanDates';
import { useAPIPayload } from '../../core-utils/helper-utils/apiPayloadHelper';
import TrendAnalysisPage from '../TrendAnalysisPage';
import BasicModal from '../../_components/atoms/BasicModal';
import ReportCreateNewLabelField from '../../_components/atoms/ReportCreateNewLabelField';
import {
  formatTrendTableData,
  TREND_ANALYSIS_TABLE_COLUMNS,
  trendSeriesData,
  MENU_OPTIONS,
  TrendAnalysisLibraryLocationProps,
} from '../../core-utils/helper-utils/trendAnalysisHelper';
import TrendAnalysisConstants from '../../core-utils/messages/TrendAnalysisConstants';
import Divider from '../../_components/atoms/Divider';
import EditIcon from '@mui/icons-material/Edit';

export interface Document {
  document_name: string;
}
export interface ComparisonDataPros {
  _id: string;
  comparison_name?: string;
  vendor_type?: string;
  contract_ids?: string[];
  comparison_parameters?: any[];
  status:
    | 'Ready'
    | 'Failed Compilation'
    | 'Processing'
    | 'No Documents Uploaded';
  createdAt: string;
  documents: Document[];
}

const useStyles = makeStyles()({
  root: {
    display: 'flex',
    flexDirection: 'column',
  },
  titleAndSubtitle: {
    width: '1324px',
    padding: '32px 0px 32px 63px',
  },
  hr: {
    borderBottom: `1px solid ${patientAccessTheme.palette.grey[300]}`,
  },
  body: {
    width: '1324px',
    padding: '40px 0px 32px 63px',
  },
  pagination: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  noDataGrid: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  tooltip: {
    backgroundColor: patientAccessTheme.palette.grey[900],
    padding: '10px',
    fontSize: '13px',
    fontFamily: 'Inter',
    fontWeight: 400,
    lineHeight: '16px',
    marginTop: '2px',
    color: patientAccessTheme.palette.status.white,
  },
  arrow: {
    color: patientAccessTheme.palette.grey[900],
    marginLeft: '50px',
  },
  popper: {
    marginTop: '-4px',
    marginLeft: '-34px',
  },
  preview: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    height: '440px',
  },
  badgeRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
    marginTop: '4px',
  },
  badge: {
    fontSize: '11px',
    padding: '2px 8px',
    borderRadius: '100px',
    fontWeight: 500,
    marginRight: '6px',
  },
  metricSection: {
    marginTop: '8px',
  },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    marginTop: '4px',
  },
  metricLabel: {
    marginBottom: '6px',
  },
  chip: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    fontSize: '12px',
    background: patientAccessTheme.palette.grey[100],
    border: `0.5px solid ${patientAccessTheme.palette.grey[300]}`,
    borderRadius: '100px',
    padding: '2px 10px',
  },
  dot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  flex: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
});

const TrendAnalysisLibraryPage: React.FC<{}> = observer(({}) => {
  //Stores
  const employerGroupStore = useStores().employerGroupStore;
  const quarterlyReportStore = useStores().quaterlyReportStore;
  const loginPageStore = useStores().loginPageStore;

  // Constants
  const { classes } = useStyles();
  const location: TrendAnalysisLibraryLocationProps = useLocation();
  const { TITLE, SUBTITLE, card } = RECOMMENDED_APPS.TREND_ANALYSIS;
  const navigate = useNavigate();
  const pageLimit = 10;

  const {
    incurredStartDate,
    incurredEndDate,
    paidStartDate,
    paidEndDate,
    isIncurredDate,
    isPaidDate,
    isPlanDatesFetched,
    aggregationParameter,
  } = quarterlyReportStore;

  const { getPlanDates } = usePlanDates();
  const payload = useAPIPayload();
  const abortController = new AbortController();

  // States
  const [comparisonData, setComparisonData] = useState<ComparisonDataPros[]>(
    [],
  );
  const [trendRawData, setTrendRawData] = useState<[]>([]);
  const [mainLoading, setMainLoading] = useState(true);
  const [pageCount, setPageCount] = useState(10);
  const [tablePageNumber, setTablePageNumber] = useState(1);
  const [trendName, setTrendName] = useState<string>('');

  const [openNewTrendModal, setOpenNewTrendModal] = useState<boolean>(false);
  const [trendAnalysisModalOpen, setTrendAnalysisModalOpen] = useState<boolean>(
    false,
  );
  const [editTrend, setEditTrend] = useState<any>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [trendList, setTrendList] = useState<any[]>([]);
  const [openTrendPreviewModal, setOpenTrendPreviewModal] = useState(false);
  const [editTrendIndex, setEditTrendIndex] = useState<number | null>(null);
  const [editNameModal, setEditNameModal] = useState<{
    open: boolean;
    type: 'trend' | null;
    index: number | null;
    value: string;
    color?: string;
  }>({ open: false, type: null, index: null, value: '', color: undefined });
  const [editSourceComparisonId, setEditSourceComparisonId] = useState<
    string | null
  >(null);

  // Functions
  const handlePagination = (_event: ChangeEvent<unknown>, page: number) => {
    setTablePageNumber(page);
  };

  const handleRowClick = (
    eg_nid: DueDiligenceDataType['eg_nid'],
    status: DueDiligenceDataType['status'] | compilationDataProps['status'],
    contract_ids: ComparisonDataPros['contract_ids'],
    comparison_name: ComparisonDataPros['comparison_name'],
    vendor_type: ComparisonDataPros['vendor_type'],
    comparison_id: ComparisonDataPros['_id'],
  ) => {
    const selectedComparison: any = trendRawData.find(
      (item: any) => item._id === comparison_id,
    );
    quarterlyReportStore.setAggregationParameter(
      selectedComparison?.aggregationParameter,
    );
    quarterlyReportStore.setIncurredStartDate(
      selectedComparison?.incurredStartDate,
    );
    quarterlyReportStore.setIncurredEndDate(
      selectedComparison?.incurredEndDate,
    );
    quarterlyReportStore.setPaidStartDate(selectedComparison?.paidStartDate);
    quarterlyReportStore.setPaidEndDate(selectedComparison?.paidEndDate);
    navigate(TREND_ANALYSIS_INTERNAL, {
      state: {
        trend_id: selectedComparison?._id,
      },
    });
  };

  const handleMenuSelect = async (row: any, option: string) => {
    const selectedComparison: any = trendRawData.find(
      (item: any) => item._id === row._id,
    );
    switch (option) {
      case 'View Trend':
        navigate(TREND_ANALYSIS_INTERNAL, {
          state: {
            trend_id: selectedComparison?._id,
          },
        });
        break;
      case 'Edit':
        handleEdit(selectedComparison);
        break;
      case 'Delete':
        await axios.post(
          properties.aiqBaseUrl +
            properties.deepDiveBaseUrl +
            properties.deleteTrend,
          {
            trend_id: row._id,
          },
        );
        getAllTrends();
        break;
      default:
        break;
    }
  };

  const handleEdit = (selectedComparison: any) => {
    setTrendList(selectedComparison?.trendList || []);
    setEditTrend(selectedComparison);
    setTrendName(selectedComparison?.trendName || '');
    setIsEditMode(true);
    setOpenTrendPreviewModal(true);
  };

  const handleTrendSave = (
    timeGrain: string,
    primaryAxisData: trendSeriesData,
    isSecondaryAxis?: boolean,
    secondaryAxisData?: trendSeriesData,
  ) => {
    if (!trendName) {
      return;
    }
    const updatedTrend = {
      trendName,
      timeGrain,
      primaryAxisData,
      isSecondaryAxis,
      ...(isSecondaryAxis ? { secondaryAxisData } : {}),
    };

    if (editTrendIndex !== null) {
      // ✅ UPDATE existing trend
      setTrendList((prev) =>
        prev.map((item, index) =>
          index === editTrendIndex ? updatedTrend : item,
        ),
      );
      setEditTrendIndex(null);
    } else {
      // ✅ ADD new trend
      setTrendList((prev) => [...prev, updatedTrend]);
    }
    setTrendAnalysisModalOpen(false);
    setOpenTrendPreviewModal(true);
  };

  const handleEditTrend = (index: number) => {
    setEditTrendIndex(index);
    setTrendName(trendList[index].trendName);
    setTrendAnalysisModalOpen(true);
  };

  const handleEditNameSave = () => {
    switch (editNameModal.type) {
      case 'trend':
        if (editNameModal.index !== null) {
          setTrendList((prev) =>
            prev.map((item, i) =>
              i === editNameModal.index
                ? { ...item, trendName: editNameModal.value }
                : item,
            ),
          );
          setTrendName(editNameModal.value);
        }
        break;
      default:
        break;
    }
    setEditNameModal({ open: false, type: null, index: null, value: '' });
  };

  const handleEditCancel = () => {
    if (editSourceComparisonId) {
      navigate(TREND_ANALYSIS_INTERNAL, {
        state: { trend_id: editSourceComparisonId },
      });
      setEditSourceComparisonId(null);
    }
    // reset edit state
    setIsEditMode(false);
    setEditTrend(null);
    setTrendList([]);
    setTrendName('');
    setOpenTrendPreviewModal(false);
  };

  // Apis
  const handleSubmit = async () => {
    const newTrend = await axios.post(
      properties.aiqBaseUrl +
        properties.deepDiveBaseUrl +
        properties.createOrUpdateTrend,
      {
        ...(editTrend && { trend_id: editTrend?._id }),
        trendName,
        ...payload,
        trendList,
        createdBy: `${loginPageStore.firstName} ${loginPageStore.lastName}`,
        updatedBy: `${loginPageStore.firstName} ${loginPageStore.lastName}`,
      },
    );

    setTrendAnalysisModalOpen(false);
    setIsEditMode(false);
    setEditTrend(null);
    setEditSourceComparisonId(null);
    navigate(TREND_ANALYSIS_INTERNAL, {
      state: {
        trend_id: newTrend.data?.data?._id,
      },
    });
  };

  const getAllTrends = async () => {
    setMainLoading(true);
    try {
      const res = await axios.post(
        properties.aiqBaseUrl +
          properties.deepDiveBaseUrl +
          properties.getAllTrends,
        {
          ...payload,
          page: tablePageNumber,
          limit: pageLimit,
        },
      );
      setTrendRawData(res?.data?.data || []);
      const formattedData = formatTrendTableData(res?.data?.data || []);
      setComparisonData(formattedData);
      setPageCount(Math.ceil(res.data.totalCount / pageLimit));
    } catch (error) {
      console.error('Error fetching trends:', error);
    } finally {
      setMainLoading(false);
    }
  };

  // UseEffects
  useEffect(() => {
    getAllTrends();
    return () => {
      abortController.abort();
    };
  }, [
    tablePageNumber,
    isPlanDatesFetched,
    isIncurredDate,
    isPaidDate,
    incurredStartDate,
    incurredEndDate,
    paidStartDate,
    paidEndDate,
    aggregationParameter,
  ]);

  useEffect(() => {
    const fetchPlanDates = async () => {
      if (!isPlanDatesFetched) {
        await getPlanDates();
      }
    };
    if (!employerGroupStore?.employerGroupResponseData?.eg_nid[0]) {
      return;
    }
    fetchPlanDates();
  }, [employerGroupStore?.employerGroupResponseData?.eg_nid]);

  useEffect(() => {
    if (location.state?.triggerEdit) {
      setEditSourceComparisonId(location.state.triggerEdit._id); // ← capture id
      handleEdit(location.state.triggerEdit);
      navigate(`${window.location.pathname}${window.location.search}`, {
        replace: true,
        state: {},
      });
    }
  }, [location.state?.triggerEdit]);

  return (
    <Grid container className={classes.root}>
      <Grid className={classes.titleAndSubtitle}>
        <PageTitleAndSubTitle
          title={TITLE}
          subTitle={SUBTITLE}
          customButton={true}
          customButtonText={TrendAnalysisConstants.NEW}
          customButtonClick={() => {
            setOpenNewTrendModal(true);
          }}
          customButtonWidth="170px"
          customButtonVariant={'contained'}
        />
      </Grid>
      <div className={classes.hr} />
      <Grid className={classes.body}>
        <CardComponent
          title={
            <Typography
              fontSize="14px"
              fontType="semiBold"
              textColor={patientAccessTheme.palette.text.highemphasis}
            >
              {card.title}
            </Typography>
          }
          subTitle={card.subTitle}
          body={
            <Grid
              style={{
                marginTop: '10px',
              }}
            >
              {mainLoading ? (
                <Loader height="300px" />
              ) : comparisonData?.length > 0 ? (
                <Grid>
                  <ComparisonTable
                    columns={TREND_ANALYSIS_TABLE_COLUMNS}
                    rowsData={comparisonData}
                    handleRowClick={handleRowClick}
                    menuOptions={MENU_OPTIONS}
                    showMenu={true}
                    onMenuSelect={handleMenuSelect}
                  />
                  <Box className={classes.pagination}>
                    <Pagination
                      count={pageCount}
                      page={tablePageNumber}
                      boundaryCount={1}
                      color="primary"
                      size="small"
                      shape="circular"
                      onChange={handlePagination}
                    />
                  </Box>
                </Grid>
              ) : (
                <Grid className={classes.noDataGrid}>
                  <NoDoc />
                  <Typography
                    fontSize="16px"
                    fontType="semiBold"
                    textColor={patientAccessTheme.palette.text.highemphasis}
                    margin="16px 0px 10px 0px"
                  >
                    {card.noData.title}
                  </Typography>
                  <Typography
                    fontSize="14px"
                    fontType="regular"
                    textColor={patientAccessTheme.palette.text.mediumemphasis}
                  >
                    {card.noData.subTitle}
                  </Typography>
                </Grid>
              )}
            </Grid>
          }
        />
      </Grid>
      <BasicModal
        open={openNewTrendModal}
        title={TrendAnalysisConstants.NEW_TREND}
        isSaveButton={true}
        saveBtnText={TrendAnalysisConstants.NEW_TREND}
        saveBtnAction={() => {
          setOpenNewTrendModal(false);
          setTrendAnalysisModalOpen(true);
        }}
        saveBtnDisabled={!trendName}
        isCloseBtn={trendList?.length > 0 ? true : false}
        closeBtnText={TrendAnalysisConstants.CANCEL}
        handleClose={() => {
          setOpenNewTrendModal(false);
          setTrendName('');
        }}
        height="auto"
        width="auto"
      >
        <Typography>{TrendAnalysisConstants.TREND_NAME}</Typography>
        <ReportCreateNewLabelField
          label=""
          width="100%"
          component={
            <TextField
              value={trendName}
              placeholder={TrendAnalysisConstants.TREND_PLACEHOLDER}
              height="36px"
              width="100%"
              fullWidth
              onChange={(e: any) => {
                setTrendName(e.target.value);
              }}
            />
          }
          marginBottom="12px"
        />
      </BasicModal>
      {trendAnalysisModalOpen && (
        <TrendAnalysisPage
          isModal={true}
          open={trendAnalysisModalOpen}
          handleClose={() => {
            setTrendAnalysisModalOpen(false);
            if (editTrendIndex !== null) {
              setOpenTrendPreviewModal(true);
              setEditTrendIndex(null);
            }
          }}
          handleModalSubmit={handleTrendSave}
          modalPrimaryAxisData={
            editTrendIndex !== null
              ? trendList[editTrendIndex]?.primaryAxisData
                ? JSON.parse(
                    JSON.stringify(trendList[editTrendIndex]?.primaryAxisData),
                  )
                : null
              : JSON.parse(
                  JSON.stringify(
                    TrendAnalysisConstants?.DEFAULT_PRIMARY_AXIS_DATA,
                  ),
                )
          }
          modalSecondaryAxisData={
            editTrendIndex !== null
              ? trendList[editTrendIndex]?.secondaryAxisData
                ? JSON.parse(
                    JSON.stringify(
                      trendList[editTrendIndex]?.secondaryAxisData,
                    ),
                  )
                : null
              : JSON.parse(
                  JSON.stringify(
                    TrendAnalysisConstants?.DEFAULT_SECONDARY_AXIS_DATA,
                  ),
                )
          }
          modalIsSecondaryAxis={
            editTrendIndex !== null
              ? trendList[editTrendIndex]?.isSecondaryAxis
              : false
          }
          modalTimeGrain={
            editTrendIndex !== null
              ? trendList[editTrendIndex]?.timeGrain
              : 'Monthly'
          }
          isEdit={editTrendIndex !== null || !!editTrend}
        />
      )}
      <BasicModal
        open={openTrendPreviewModal}
        handleClose={
          isEditMode ? handleEditCancel : () => setOpenTrendPreviewModal(false)
        }
        title={TrendAnalysisConstants.TREND_PREVIEW}
        isSaveButton={true}
        saveBtnText={
          isEditMode
            ? TrendAnalysisConstants.UPDATE_ANALYSIS
            : TrendAnalysisConstants.CREATE_ANALYSIS
        }
        saveBtnAction={handleSubmit}
        saveBtnDisabled={trendList.length === 0}
        isCloseBtn={false}
        width="600px"
        height="600px"
        isScrollable={true}
      >
        <Grid className={classes.preview}>
          {trendList.map((trend, index) => {
            const {
              trendName,
              timeGrain,
              primaryAxisData,
              isSecondaryAxis,
              secondaryAxisData,
            } = trend;
            const { unit, metrics } = primaryAxisData;
            const secondaryUnit = isSecondaryAxis
              ? secondaryAxisData?.unit
              : null;
            const secondaryMetrics = isSecondaryAxis
              ? secondaryAxisData?.metrics
              : [];

            return (
              <GeneralCardComponent
                key={index}
                title={
                  <Grid className={classes.flex}>
                    <span>{`Trend ${index + 1}: ${trendName}`}</span>
                    <EditIcon
                      style={{ cursor: 'pointer', fontSize: '16px' }}
                      onClick={() =>
                        setEditNameModal({
                          open: true,
                          type: 'trend',
                          index,
                          value: trendName,
                        })
                      }
                    />
                  </Grid>
                }
                titleRightMostItem={
                  <>
                    <Edit
                      onClick={() => handleEditTrend(index)}
                      style={{ cursor: 'pointer' }}
                    />
                  </>
                }
                body={
                  <Grid>
                    <Grid className={classes.badgeRow}>
                      {[
                        {
                          label: `Time Grain: ${timeGrain}`,
                          bg: '#E6F1FB',
                          color: '#185FA5',
                        },
                      ].map(({ label, bg, color }) => (
                        <span
                          key={label}
                          className={classes.badge}
                          style={{ background: bg, color }}
                        >
                          {label}
                        </span>
                      ))}
                    </Grid>
                    <Divider marginTop={'15px'} marginBottom={'15px'} />
                    {/* Metadata badges */}
                    <Grid className={classes.badgeRow}>
                      {[
                        {
                          label: `Primary Axis - Unit: ${unit}`,
                          bg: '#F1EFE8',
                          color: '#5F5E5A',
                        },
                      ].map(({ label, bg, color }) => (
                        <span
                          key={label}
                          className={classes.badge}
                          style={{ background: bg, color }}
                        >
                          {label}
                        </span>
                      ))}
                    </Grid>

                    {/* Metrics */}
                    {metrics.map((metric: any, mIdx: number) => (
                      <Grid key={mIdx} className={classes.metricSection}>
                        <Typography
                          fontSize="12px"
                          fontType="semiBold"
                          textColor={
                            patientAccessTheme.palette.text.mediumemphasis
                          }
                          margin="0 0 4px"
                        >
                          {metric.name}
                        </Typography>
                        <Grid className={classes.chipRow}>
                          {
                             
                            metric.options.map((opt: any, oIdx: number) => (
                              <span key={oIdx} className={classes.chip}>
                                <Typography
                                  fontSize="12px"
                                  fontType="regular"
                                  textColor={
                                    patientAccessTheme.palette.text.highemphasis
                                  }
                                >
                                  {opt.name}
                                </Typography>
                              </span>
                            ))
                          }
                        </Grid>
                      </Grid>
                    ))}
                    <>
                      {isSecondaryAxis && (
                        <>
                          <Divider marginTop={'15px'} marginBottom={'15px'} />
                          <Grid className={classes.badgeRow}>
                            {[
                              {
                                label: `Secondary Axis - Unit: ${secondaryUnit}`,
                                bg: '#F1EFE8',
                                color: '#5F5E5A',
                              },
                            ].map(({ label, bg, color }) => (
                              <span
                                key={label}
                                className={classes.badge}
                                style={{ background: bg, color }}
                              >
                                {label}
                              </span>
                            ))}
                          </Grid>

                          {secondaryMetrics.map((metric: any, mIdx: number) => (
                            <Grid key={mIdx} className={classes.metricSection}>
                              <Typography
                                fontSize="12px"
                                fontType="semiBold"
                                textColor={
                                  patientAccessTheme.palette.text.mediumemphasis
                                }
                                margin="0 0 4px"
                              >
                                {metric.name}
                              </Typography>
                              <Grid className={classes.chipRow}>
                                {
                                   
                                  metric.options.map(
                                    (opt: any, oIdx: number) => (
                                      <span key={oIdx} className={classes.chip}>
                                        <Typography
                                          fontSize="12px"
                                          fontType="regular"
                                          textColor={
                                            patientAccessTheme.palette.text
                                              .highemphasis
                                          }
                                        >
                                          {opt.name}
                                        </Typography>
                                      </span>
                                    ),
                                  )
                                }
                              </Grid>
                            </Grid>
                          ))}
                        </>
                      )}
                    </>
                  </Grid>
                }
                mainGridStyles={{ minHeight: 'fit-content' }}
              />
            );
          })}
        </Grid>
      </BasicModal>
      <BasicModal
        open={editNameModal.open}
        handleClose={() =>
          setEditNameModal({ open: false, type: null, index: null, value: '' })
        }
        title={'Edit Trend Name'}
        isSaveButton={true}
        saveBtnText="Save"
        saveBtnAction={handleEditNameSave}
        saveBtnDisabled={!editNameModal.value.trim()}
        isCloseBtn={true}
        height="auto"
        width="auto"
      >
        <ReportCreateNewLabelField
          label={TrendAnalysisConstants.TREND_NAME}
          width="100%"
          component={
            <Grid style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <TextField
                value={editNameModal.value}
                placeholder="Enter name"
                height="36px"
                width="100%"
                fullWidth
                onChange={(e: any) =>
                  setEditNameModal((prev) => ({
                    ...prev,
                    value: e.target.value,
                  }))
                }
              />
            </Grid>
          }
          marginBottom="12px"
        />
      </BasicModal>
    </Grid>
  );
});

export default TrendAnalysisLibraryPage;
